import {
  useLandlordResearchPrompt,
  useLandlordResearchPromptDiff,
  useLeads,
} from "@inrange/building-manager-api-client";
import CollapsibleCard from "../site/CollapsibleCard";
import LandlordTestPanel from "./LandlordTestPanel";
import PromptRuleSetEditor from "./PromptRuleSetEditor";

// The landlord-research agent's editable rule-set prompt. A thin task binding over the
// shared PromptRuleSetEditor: wires the landlord prompt query + its write mutations
// (from useLeads) and supplies the landlord-specific copy + test panel.
const LandlordResearchPromptCard = () => {
  const prompt = useLandlordResearchPrompt();
  const {
    saveLandlordPromptRuleMut,
    deleteLandlordPromptRuleMut,
    reorderLandlordPromptRulesMut,
    saveLandlordPromptPreambleMut,
    saveLandlordPromptAddendumMut,
    restoreLandlordPromptRevisionMut,
  } = useLeads();

  const writing =
    saveLandlordPromptRuleMut.isLoading ||
    deleteLandlordPromptRuleMut.isLoading ||
    reorderLandlordPromptRulesMut.isLoading ||
    saveLandlordPromptPreambleMut.isLoading ||
    saveLandlordPromptAddendumMut.isLoading ||
    restoreLandlordPromptRevisionMut.isLoading;

  return (
    <CollapsibleCard title="Landlord research prompt" initialOpen={false}>
      <PromptRuleSetEditor
        prompt={prompt}
        writing={writing}
        saveRuleLoading={saveLandlordPromptRuleMut.isLoading}
        savePreambleLoading={saveLandlordPromptPreambleMut.isLoading}
        compiledPreviewLabel="Compiled prompt (what the agent sees)"
        loadFailedMessage="Could not load the landlord-research prompt."
        ruleCategoryPlaceholder="e.g. Method, Confidence, Search budget"
        description={
          <>
            The landlord-research agent&apos;s editable prompt is a{" "}
            <strong>preamble</strong> plus a set of addressable{" "}
            <strong>rules</strong> (each with why it exists, its limits and the
            evidence behind it). Every change appends a new revision; every
            researched result is stamped with the revision that produced it. The
            data-residency guardrail below is always appended and cannot be edited
            away.
          </>
        }
        emptyRulesHint={
          <>
            No rules on this revision - the whole prompt is in the preamble above (it
            was saved as free text, before the rule structure). The shipped default
            now ships as a structured rule set: use{" "}
            <strong>Reset whole prompt to shipped default</strong> above to adopt it,
            or <strong>+ Add rule</strong> to break this prompt out yourself (each
            rule with its own why / limits / evidence).
          </>
        }
        onSaveRule={(a) => saveLandlordPromptRuleMut.mutateAsync(a)}
        onDeleteRule={(a) => deleteLandlordPromptRuleMut.mutateAsync(a)}
        onReorderRules={(a) => reorderLandlordPromptRulesMut.mutateAsync(a)}
        onSavePreamble={(a) => saveLandlordPromptPreambleMut.mutateAsync(a)}
        onResetToDefault={(a) => saveLandlordPromptAddendumMut.mutateAsync(a)}
        onRestoreRevision={(a) => restoreLandlordPromptRevisionMut.mutateAsync(a)}
        useDiff={useLandlordResearchPromptDiff}
        renderTestPanel={(addendum) => <LandlordTestPanel addendum={addendum} />}
      />
    </CollapsibleCard>
  );
};

export default LandlordResearchPromptCard;
